import { inject, Injectable } from "@angular/core";
import { NavigationEnd, Router } from '@angular/router';
import { ApplicationInsights, IExceptionTelemetry } from "@microsoft/applicationinsights-web";
import { EMPTY } from "rxjs";
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { APPINSIGHTS_KEY, TARGET } from '../types/constants/appconstants';
import { User } from '../types/user';
import { AuthService } from './data/auth.service';
import { UserService } from './data/user.service';
import { UnsubscribeOnDestroy } from "../classes";

@Injectable({
    "providedIn": "root"
})
export class ApplicationInsightsService extends UnsubscribeOnDestroy {
    private router = inject(Router);
    private userService = inject(UserService);
    private authService = inject(AuthService);
    private appInsightsKey = inject(APPINSIGHTS_KEY);
    private target = inject(TARGET);
    private appInsights: ApplicationInsights;
    
    constructor() {
        super();
        this.appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: this.appInsightsKey,
                enableCorsCorrelation: true
            }
        });

        if (this.target !== "local") {
            this.authService.isUserLoggedIn()
                .pipe(
                    filter((loggedIn) => loggedIn),
                    take(1),
                    switchMap(() => this.authService.whenAccessTokenReady()),
                    switchMap(() => this.userService.getMyUserProfile()),
                    catchError(() => EMPTY),
                    takeUntil(this.ngUnsubscribe$)
                )
                .subscribe((user: User) => {
                    this.appInsights.loadAppInsights();
                    this.loadCustomTelemetryProperties();
                    this.setUserId(user.userName);
                });

            this.createRouterSubscription();
        }

    }

    setUserId(userId: string) {
        this.appInsights.setAuthenticatedUserContext(userId, "", true);
    }

    clearUserId() {
        this.appInsights.clearAuthenticatedUserContext();
    }

    logPageView(name?: string, uri?: string) {
        this.appInsights.trackPageView({
            name,
            uri
        });
    }

    logException(error: Error) {
        let exception: IExceptionTelemetry = {
            exception: error
        };
        this.appInsights?.trackException(exception);
    }

    private loadCustomTelemetryProperties() {
        this.appInsights.addTelemetryInitializer(envelope => {
            var item = envelope.baseData;
            item.properties = item.properties || {};
            item.properties["ApplicationPlatform"] = "Web";
            item.properties["ApplicationName"] = "ApplicationName";
        });
    }

    private createRouterSubscription() {
        this.router.events
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                this.logPageView(null, event.urlAfterRedirects);
            });
    }
}
